import { captureException } from "@sentry/browser";
import debounce from "lodash.debounce";
import qs from "query-string";
import { useCallback, useEffect, useState } from "react";

import { getGpsLocation, type LocationType } from "@/helpers/location";
import { cn } from "@/lib/utils";

import { LocateIcon } from "./icons";

interface PredictionType {
  description: string;
  place_id: string;
}

interface PredictionsProps extends React.ComponentProps<"ul"> {
  className?: string;
  onClose(): void;
  onLocation(newLocation: LocationType): void;
  onProgress(): void;
  onQuery(query: string): void;
  query: string;
}

export default function Predictions({
  className,
  onClose,
  onLocation,
  onQuery,
  onProgress,
  query,
  ...props
}: PredictionsProps) {
  const [predictions, setPredictions] = useState<PredictionType[]>([]);

  const fetchPredictions = async (query: string) => {
    const response = await fetch(
      `${process.env.API_BASE_URL}/rest/places?${qs.stringify({ query })}`
    );
    const { data } = await response.json();
    setPredictions(data);
  };

  const handleCurrentLocation = async () => {
    onClose();
    onQuery("");

    try {
      const newLocation = await getGpsLocation();
      if (newLocation) {
        onLocation(newLocation);
      }
    } catch (error) {
      captureException(error);
    }
  };

  const handlePlace = (placeId: string) => async () => {
    onProgress();
    const response = await fetch(
      `${process.env.API_BASE_URL}/rest/place/${placeId}`
    );
    const { data } = await response.json();
    const { formatted_address, geometry } = data;
    const { lat, lng } = geometry.location;
    onQuery(formatted_address);
    onLocation({
      address: formatted_address,
      location: `${lat},${lng}`,
    });
    onClose();
  };

  const debouncedFetchPredictions = useCallback(
    debounce(fetchPredictions, 500),
    []
  );

  useEffect(
    () => () => debouncedFetchPredictions.cancel(),
    [debouncedFetchPredictions]
  );

  useEffect(() => {
    if (query === "") {
      setPredictions([]);
    } else {
      debouncedFetchPredictions(query);
    }
  }, [query, debouncedFetchPredictions]);

  return (
    <ul
      className={cn(
        "absolute top-1/1 left-0 z-1 w-full rounded-md border border-black/10 bg-white md:min-w-100",
        className
      )}
      {...props}
    >
      {/** biome-ignore lint/a11y/useKeyWithClickEvents: yeah, its okay */}
      {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy */}
      <li
        className="flex cursor-pointer items-center justify-start px-3 py-1.5 text-black hover:bg-black/6"
        onClick={handleCurrentLocation}
      >
        <LocateIcon className="mr-2" />
        Current Location
      </li>
      {predictions.map(({ description, place_id }) => (
        // biome-ignore lint/a11y/useKeyWithClickEvents: no worry
        // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
        <li
          className="line-clamp-1 w-full cursor-pointer px-3 py-1.5 text-left text-black hover:bg-black/6"
          key={place_id}
          onClick={handlePlace(place_id)}
        >
          {description}
        </li>
      ))}
    </ul>
  );
}
